import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Save, Mail, Eye, Send } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const TEMPLATE_LABELS = {
  booking_request_customer: "Buchungsanfrage → Kunde",
  booking_salon_notification: "Buchungsanfrage → Salon",
  booking_confirmed: "Bestätigung → Kunde",
  booking_cancelled: "Stornierung → Kunde",
  booking_completed: "Abschluss → Kunde",
  booking_alternative: "Alternativ-Vorschlag → Kunde",
  form_submission_salon: "Formular-Eingang → Salon",
  loyalty_reward_unlocked: "Treuepunkte: Belohnung freigeschaltet → Kunde",
  loyalty_points_expiring: "Treuepunkte: Punkte verfallen bald → Kunde",
  loyalty_birthday_bonus: "Treuepunkte: Geburtstags-Bonus → Kunde",
};

const VARIABLES = {
  booking_request_customer: ["customer_name", "salon_name", "service_name", "addons", "addons_line", "date", "time", "total_duration", "total_price", "staff_line", "staff_name"],
  booking_salon_notification: ["customer_name", "customer_email", "customer_phone", "service_name", "date", "time", "total_duration", "total_price", "staff_line", "notes_line"],
  booking_confirmed: ["customer_name", "salon_name", "service_name", "addons_line", "date", "time", "staff_line"],
  booking_cancelled: ["customer_name", "salon_name", "date", "time"],
  booking_completed: ["customer_name", "salon_name"],
  booking_alternative: ["customer_name", "salon_name", "date", "time", "proposed_date", "proposed_time", "alternative_message"],
  form_submission_salon: ["form_name", "fields_text", "salon_name"],
  loyalty_reward_unlocked: ["customer_name", "salon_name", "points_balance", "reward_name", "reward_cost", "account_url"],
  loyalty_points_expiring: ["customer_name", "salon_name", "expiring_points", "expiry_date", "points_balance", "account_url"],
  loyalty_birthday_bonus: ["customer_name", "salon_name", "bonus_points", "points_balance"],
};

export default function EmailTemplates() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState({ subject: "", body: "" });
  const [previewLoading, setPreviewLoading] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testBusy, setTestBusy] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = async () => {
    const { data } = await api.get("/cms/email-templates");
    setList(data);
    if (data[0] && !active) setActive(data[0]);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { setTestTo(user?.email || ""); }, [user]);

  // Live preview: debounced render of subject/body using sample data
  const draftKey = useMemo(() => active ? `${active.key}|${active.subject}|${active.body}` : "", [active]);
  useEffect(() => {
    if (!active) return;
    setPreviewLoading(true);
    const handle = setTimeout(async () => {
      try {
        const { data } = await api.post(`/cms/email-templates/${active.key}/preview`, {
          subject: active.subject, body: active.body,
        });
        setPreview({ subject: data.subject, body: data.body });
      } catch {
        setPreview({ subject: "(Vorschau-Fehler)", body: "" });
      } finally {
        setPreviewLoading(false);
      }
    }, 350);
    return () => clearTimeout(handle);
  }, [draftKey, active]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/cms/email-templates/${active.key}`, {
        subject: active.subject,
        body: active.body,
        enabled: active.enabled !== false,
      });
      load();
    } finally { setBusy(false); }
  };

  const sendTest = async () => {
    if (!active) return;
    setTestBusy(true);
    setTestResult(null);
    try {
      const { data } = await api.post(`/cms/email-templates/${active.key}/test-send`, {
        subject: active.subject, body: active.body, to: testTo || null,
      });
      setTestResult({ ok: data.ok, status: data.status, to: data.to, error: data.error });
    } catch (e) {
      setTestResult({ ok: false, status: "failed", error: e?.response?.data?.detail || "Fehler" });
    } finally {
      setTestBusy(false);
    }
  };

  if (!active) return <div className="p-8">Lade...</div>;
  const vars = VARIABLES[active.key] || [];

  return (
    <div className="p-8 lg:p-12 mv-fade-in max-w-7xl">
      <div className="flex items-end justify-between mb-10">
        <div><p className="mv-label mb-2">Texte</p><h1 className="font-display text-4xl">E-Mail-Vorlagen</h1></div>
        <button data-testid="save-template-btn" onClick={save} disabled={busy} className="mv-btn"><Save className="w-4 h-4" /> {busy ? "..." : "Speichern"}</button>
      </div>

      <div className="grid lg:grid-cols-[220px_1fr_1fr] gap-6">
        <div>
          <p className="mv-label mb-3">Vorlagen</p>
          <div className="space-y-1">
            {list.map((t) => (
              <button key={t.key} data-testid={`tpl-${t.key}`} onClick={() => setActive(t)} className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-colors ${active.key === t.key ? "bg-[var(--mv-primary)] text-white" : "hover:bg-[var(--mv-surface)]"}`}>
                <span className="flex items-center gap-2"><Mail className="w-3.5 h-3.5" strokeWidth={1.5} /> {TEMPLATE_LABELS[t.key] || t.key}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-4">
          <div className="mv-card p-6">
            <label className="flex items-center justify-between mb-4 cursor-pointer">
              <div>
                <p className="text-sm font-medium" style={{ color: "var(--mv-text)" }}>Vorlage aktiv</p>
                <p className="text-xs mt-0.5" style={{ color: "var(--mv-text-muted)" }}>
                  Wenn deaktiviert, wird diese E-Mail nicht mehr automatisch versendet (Test-Sends bleiben möglich).
                </p>
              </div>
              <input
                type="checkbox"
                data-testid="template-enabled"
                checked={active.enabled !== false}
                onChange={(e) => setActive({ ...active, enabled: e.target.checked })}
                className="w-5 h-5 cursor-pointer"
              />
            </label>
            <p className="mv-label mb-2">Betreff</p>
            <input data-testid="template-subject" className="mv-input" value={active.subject} onChange={(e) => setActive({ ...active, subject: e.target.value })} />

            <p className="mv-label mb-2 mt-5">Inhalt</p>
            <textarea data-testid="template-body" className="mv-input min-h-[320px] font-mono text-sm leading-relaxed" value={active.body} onChange={(e) => setActive({ ...active, body: e.target.value })} />

            <p className="mv-label mt-5 mb-2">Verfügbare Variablen</p>
            <div className="flex flex-wrap gap-1.5">
              {vars.map((v) => (
                <button key={v} type="button" onClick={() => setActive({ ...active, body: active.body + `{{${v}}}` })} className="px-2.5 py-1 rounded-md text-xs border hover:bg-[var(--mv-surface)] font-mono" style={{ borderColor: "var(--mv-border)" }}>
                  {`{{${v}}}`}
                </button>
              ))}
            </div>
            <p className="text-xs mt-3" style={{ color: "var(--mv-text-muted)" }}>Klicken Sie eine Variable an, um sie an den Cursor (Ende) einzufügen.</p>
          </div>
        </div>

        <div className="space-y-4" data-testid="preview-panel">
          <div className="mv-card p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="mv-label flex items-center gap-1.5"><Eye className="w-3.5 h-3.5" strokeWidth={1.5} /> Vorschau</p>
              {previewLoading && <span className="text-xs" style={{ color: "var(--mv-text-muted)" }}>rendert…</span>}
            </div>
            <p className="text-xs mb-3" style={{ color: "var(--mv-text-muted)" }}>Platzhalter werden mit Beispieldaten ersetzt.</p>

            <div className="rounded-lg border overflow-hidden" style={{ borderColor: "var(--mv-border)" }}>
              <div className="px-4 py-2.5 text-xs" style={{ background: "var(--mv-surface)", color: "var(--mv-text-muted)" }}>
                <b style={{ color: "var(--mv-text)" }}>Betreff:</b>{" "}
                <span data-testid="preview-subject">{preview.subject || "(leer)"}</span>
              </div>
              <pre data-testid="preview-body" className="p-4 text-sm leading-relaxed whitespace-pre-wrap font-sans" style={{ background: "#fff", color: "var(--mv-text)", margin: 0, fontFamily: "inherit" }}>
                {preview.body || "(leer)"}
              </pre>
            </div>
          </div>

          <div className="mv-card p-6">
            <p className="mv-label mb-2 flex items-center gap-1.5"><Send className="w-3.5 h-3.5" strokeWidth={1.5} /> Test-E-Mail senden</p>
            <p className="text-xs mb-3" style={{ color: "var(--mv-text-muted)" }}>Sendet diese Vorlage mit Beispieldaten an die angegebene Adresse, damit du sie im Mail-Programm prüfen kannst.</p>
            <div className="flex gap-2">
              <input
                data-testid="test-to-input"
                type="email"
                placeholder="empfaenger@example.com"
                className="mv-input flex-1 text-sm"
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
              />
              <button
                data-testid="test-send-btn"
                onClick={sendTest}
                disabled={testBusy || !testTo}
                className="mv-btn"
              >
                <Send className="w-3.5 h-3.5" /> {testBusy ? "…" : "Senden"}
              </button>
            </div>
            {testResult && (
              <div
                data-testid="test-result"
                className="mt-3 text-xs px-3 py-2 rounded"
                style={{
                  background: testResult.ok ? "rgba(34,197,94,0.1)" : "rgba(220,38,38,0.1)",
                  color: testResult.ok ? "#15803d" : "#b91c1c",
                }}
              >
                {testResult.ok
                  ? `✓ Erfolgreich an ${testResult.to} gesendet.`
                  : testResult.status === "queued"
                    ? `In Warteschlange — SMTP ist evtl. nicht konfiguriert (siehe E-Mail-Einstellungen). ${testResult.error || ""}`
                    : `Fehler: ${testResult.error || "Unbekannt"}`}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
